import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Car, Loader2, Trash2, Check, Plus, Sparkles, Search, Pencil, X } from "lucide-react";

export const VehicleCatalog = () => {
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [onlyPending, setOnlyPending] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [nv, setNv] = useState({ brand: "", model: "", generation: "", years: "", ecu: "" });

  const load = () => {
    setLoading(true);
    api.get("/admin/vehicles", { params: { pending: onlyPending ? 1 : 0, q } })
      .then(({ data }) => { setRows(data.vehicles); setPendingCount(data.pending); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [onlyPending, q]);

  const add = async () => {
    if (!nv.brand.trim() || !nv.model.trim()) return toast.error("Marque et modèle requis");
    setBusy(true);
    try {
      await api.post("/admin/vehicles", nv);
      toast.success("Véhicule enregistré");
      setNv({ brand: "", model: "", generation: "", years: "", ecu: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const validate = async (id) => { await api.post(`/admin/vehicles/${id}/validate`); toast.success("Validé"); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer cette entrée ?")) return; await api.delete(`/admin/vehicles/${id}`); toast.success("Supprimé"); load(); };

  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({ brand: "", model: "", generation: "", years: "", ecu: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const startEdit = (v) => { setEditId(v.id); setEditRow({ brand: v.brand || "", model: v.model || "", generation: v.generation || "", years: v.years || "", ecu: v.ecu || "" }); };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async (validateToo = false) => {
    if (!editRow.brand.trim() || !editRow.model.trim()) return toast.error("Marque et modèle requis");
    setSavingEdit(true);
    try {
      await api.patch(`/admin/vehicles/${editId}`, { ...editRow, validate: validateToo });
      toast.success(validateToo ? "Corrigé & validé ✅" : "Modifications enregistrées ✅");
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingEdit(false); }
  };

  const inp = "bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none placeholder:text-zinc-600";

  return (
    <div className="glass rounded-md p-6 mb-6" data-testid="vehicle-catalog">
      <div className="flex items-center gap-2 mb-2">
        <Car className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Catalogue véhicules</h3>
        {pendingCount > 0 && (
          <button onClick={() => setOnlyPending((v) => !v)} data-testid="veh-pending-toggle"
            className={`ml-auto flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm transition-colors ${onlyPending ? "bg-signal-processing text-black" : "bg-signal-processing/15 text-signal-processing"}`}>
            <Sparkles className="w-3 h-3" /> {pendingCount} à valider
          </button>
        )}
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
        Catalogue Marque → Modèle → Génération utilisé au dépôt. Il <b className="text-aurora-cyan">s'enrichit automatiquement</b> des saisies clients (marquées « à valider »). Validez, corrigez ou supprimez les entrées.
      </p>

      <div className="grid sm:grid-cols-5 gap-2 mb-3" data-testid="veh-add-form">
        <input className={inp} placeholder="Marque *" value={nv.brand} onChange={(e) => setNv({ ...nv, brand: e.target.value })} data-testid="veh-brand" />
        <input className={inp} placeholder="Modèle *" value={nv.model} onChange={(e) => setNv({ ...nv, model: e.target.value })} data-testid="veh-model" />
        <input className={inp} placeholder="Génération" value={nv.generation} onChange={(e) => setNv({ ...nv, generation: e.target.value })} data-testid="veh-gen" />
        <input className={inp} placeholder="Années (2005-2013)" value={nv.years} onChange={(e) => setNv({ ...nv, years: e.target.value })} data-testid="veh-years" />
        <input className={inp} placeholder="ECU" value={nv.ecu} onChange={(e) => setNv({ ...nv, ecu: e.target.value })} data-testid="veh-ecu" />
      </div>
      <button onClick={add} disabled={busy} data-testid="veh-add-btn"
        className="flex items-center gap-1.5 px-4 py-2 mb-5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-cyan-300 transition-colors shadow-glow disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter / Mettre à jour
      </button>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input className={`${inp} w-full pl-9`} placeholder="Rechercher (marque, modèle, génération, ECU)..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="veh-search" />
      </div>

      {loading ? <div className="font-mono text-xs text-zinc-500 py-6 text-center">Chargement...</div> : (
        <div className="border border-white/10 rounded-sm overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-ink-base/95 backdrop-blur">
              <tr className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-3 py-2">Marque</th><th className="px-3 py-2">Modèle</th><th className="px-3 py-2">Génération</th><th className="px-3 py-2">Années</th><th className="px-3 py-2">ECU</th><th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                editId === v.id ? (
                  <tr key={v.id} data-testid={`veh-row-${v.id}`} className="border-b border-white/5 bg-aurora-cyan/5">
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.brand} onChange={(e) => setEditRow({ ...editRow, brand: e.target.value })} data-testid={`veh-edit-brand-${v.id}`} placeholder="Marque *" /></td>
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.model} onChange={(e) => setEditRow({ ...editRow, model: e.target.value })} data-testid={`veh-edit-model-${v.id}`} placeholder="Modèle *" /></td>
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.generation} onChange={(e) => setEditRow({ ...editRow, generation: e.target.value })} data-testid={`veh-edit-gen-${v.id}`} placeholder="Génération" /></td>
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.years} onChange={(e) => setEditRow({ ...editRow, years: e.target.value })} data-testid={`veh-edit-years-${v.id}`} placeholder="Années" /></td>
                    <td className="px-2 py-2"><input className={`${inp} w-full`} value={editRow.ecu} onChange={(e) => setEditRow({ ...editRow, ecu: e.target.value })} data-testid={`veh-edit-ecu-${v.id}`} placeholder="ECU" /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {!v.validated && (
                          <button onClick={() => saveEdit(true)} disabled={savingEdit} data-testid={`veh-save-validate-${v.id}`} title="Corriger & valider"
                            className="flex items-center gap-1 px-2 h-7 border border-signal-success/40 text-signal-success rounded-sm hover:bg-signal-success/10 transition-colors font-mono text-[9px] uppercase tracking-widest">
                            {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Valider
                          </button>
                        )}
                        <button onClick={() => saveEdit(false)} disabled={savingEdit} data-testid={`veh-save-${v.id}`} title="Enregistrer"
                          className="w-7 h-7 grid place-items-center border border-aurora-cyan/50 text-aurora-cyan rounded-sm hover:bg-aurora-cyan/10 transition-colors">{savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}</button>
                        <button onClick={cancelEdit} data-testid={`veh-cancel-${v.id}`} title="Annuler"
                          className="w-7 h-7 grid place-items-center border border-white/15 rounded-sm hover:text-white transition-colors"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                <tr key={v.id} data-testid={`veh-row-${v.id}`} className={`border-b border-white/5 ${!v.validated ? "bg-signal-processing/5" : ""}`}>
                  <td className="px-3 py-2 text-white font-medium">{v.brand}</td>
                  <td className="px-3 py-2 text-zinc-300">{v.model}</td>
                  <td className="px-3 py-2 text-aurora-cyan font-mono text-xs">{v.generation || "—"}</td>
                  <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{v.years || "—"}</td>
                  <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{v.ecu || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => startEdit(v)} data-testid={`veh-edit-${v.id}`} title="Modifier / personnaliser"
                        className="w-7 h-7 grid place-items-center border border-white/15 text-zinc-400 rounded-sm hover:border-aurora-cyan hover:text-aurora-cyan transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      {!v.validated && (
                        <button onClick={() => validate(v.id)} data-testid={`veh-validate-${v.id}`} title="Valider"
                          className="w-7 h-7 grid place-items-center border border-signal-success/40 text-signal-success rounded-sm hover:bg-signal-success/10 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                      )}
                      <button onClick={() => del(v.id)} data-testid={`veh-del-${v.id}`} title="Supprimer"
                        className="w-7 h-7 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
                )
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center font-mono text-xs text-zinc-600">Aucun véhicule</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
